import { useState, type FormEvent, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Lock, ExternalLink, Building2, ChevronDown } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { useAuthStore, type SSOProvider } from "../../stores/authStore";
import api from "../../lib/api";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";

function AnimatedGrid() {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {/* Gradient orbs */}
      <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-brand-500/8 blur-3xl animate-float-slow" />
      <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-purple-500/8 blur-3xl animate-float-slow-reverse" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full bg-brand-500/3 blur-3xl" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(99,102,241,0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(99,102,241,0.3) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
        }}
      />

      {/* Scan line */}
      <div className="absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-500/20 to-transparent animate-scan-line" />
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { login, isLoading, error, clearError, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  // SSO is gated: the user opens this disclosure, types their org slug, and we
  // fetch only that org's active providers. The login page never lists every
  // tenant's SSO.
  const [ssoOpen, setSsoOpen] = useState(false);
  const [ssoSlug, setSsoSlug] = useState("");
  const [ssoLookupResults, setSsoLookupResults] = useState<SSOProvider[] | null>(null);
  const [ssoIsLooking, setSsoIsLooking] = useState(false);
  const [ssoLookupError, setSsoLookupError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    clearError();
  }, [email, password, clearError]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch {
      /* error is set in store */
    }
  }

  async function handleSsoLookup(e: FormEvent) {
    e.preventDefault();
    const slug = ssoSlug.trim();
    if (!slug) return;
    setSsoIsLooking(true);
    setSsoLookupError(null);
    setSsoLookupResults(null);
    try {
      const res = await api.get<SSOProvider[]>("/sso/providers", {
        params: { organization_slug: slug },
      });
      setSsoLookupResults(res.data);
      if (res.data.length === 0) {
        setSsoLookupError(`No SSO configured for "${slug}". Use email + password instead.`);
      }
    } catch {
      setSsoLookupError("Couldn't look up that organization. Try again.");
    } finally {
      setSsoIsLooking(false);
    }
  }

  function handleSSO(provider: SSOProvider) {
    const redirectUri = `${window.location.origin}${window.location.pathname}#/sso/callback`;
    window.location.href = `${API_BASE_URL}/sso/authorize?provider=${encodeURIComponent(provider.provider)}&organization_id=${encodeURIComponent(provider.organization_id)}&redirect_uri=${encodeURIComponent(redirectUri)}`;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-900 p-4">
      <AnimatedGrid />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="relative mb-4"
          >
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-brand-500 to-purple-500 animate-ping opacity-10" />
            <img
              src="/files/favicon-512.png"
              alt="Asset Radar logo"
              className="relative z-10 h-14 w-14 rounded-2xl shadow-lg shadow-brand-500/30"
            />
          </motion.div>
          <h1 className="text-2xl font-bold text-surface-100">
            Welcome back
          </h1>
          <p className="mt-1 text-sm text-surface-400">
            Sign in to Asset Radar
          </p>
        </div>

        {/* Card with gradient border */}
        <div className="relative rounded-2xl p-px bg-gradient-to-b from-surface-600/50 to-transparent">
          <div className="rounded-2xl bg-surface-800/80 backdrop-blur-xl p-8 shadow-2xl shadow-black/20">
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
              >
                {error}
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                label="Email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                icon={<Mail className="h-4 w-4" />}
                required
                autoComplete="email"
              />

              <Input
                label="Password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                icon={<Lock className="h-4 w-4" />}
                required
                autoComplete="current-password"
              />

              <div className="flex items-center justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-brand-400 hover:text-brand-300 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                isLoading={isLoading}
                className="w-full"
                size="lg"
              >
                Sign In
              </Button>
            </form>

            <div className="mt-6 border-t border-surface-700 pt-4">
              <button
                type="button"
                onClick={() => {
                  setSsoOpen((v) => !v);
                  setSsoLookupError(null);
                  setSsoLookupResults(null);
                }}
                className="flex w-full items-center justify-between text-sm text-surface-400 transition-colors hover:text-surface-200"
              >
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Sign in with Single Sign-On
                </span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${ssoOpen ? "rotate-180" : ""}`}
                />
              </button>

              <AnimatePresence initial={false}>
                {ssoOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    className="overflow-hidden"
                  >
                    <form onSubmit={handleSsoLookup} className="mt-4 space-y-3">
                      <Input
                        label="Organization slug"
                        placeholder="e.g. acme"
                        value={ssoSlug}
                        onChange={(e) => setSsoSlug(e.target.value)}
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck={false}
                      />
                      <Button
                        type="submit"
                        variant="secondary"
                        size="sm"
                        className="w-full"
                        isLoading={ssoIsLooking}
                        disabled={!ssoSlug.trim()}
                      >
                        Find SSO providers
                      </Button>
                    </form>

                    {ssoLookupError && (
                      <p className="mt-3 text-xs text-amber-300">{ssoLookupError}</p>
                    )}

                    {ssoLookupResults && ssoLookupResults.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {ssoLookupResults.map((provider) => (
                          <Button
                            key={`${provider.provider}-${provider.organization_id}`}
                            variant="outline"
                            className="w-full"
                            onClick={() => handleSSO(provider)}
                          >
                            <ExternalLink className="h-4 w-4" />
                            Continue with {provider.provider}
                          </Button>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-surface-400">
          Don't have an account?{" "}
          <Link
            to="/register"
            className="text-brand-400 hover:text-brand-300 font-medium transition-colors"
          >
            Create account
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
